import React, { useState, FC, useEffect } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import './TwExpensesTab.scss';
import OtherExpenseTable from '../../pages/OtherExpenses/OtherExpenseTable/OtherExpenseTable';
import { SearchOtherExpensesParams } from '../../redux/api/otherExpenses/otherExpense.model';
import { useSearchOtherExpensesQuery } from '../../redux/api/otherExpenses/otherExpenseApi';
import CreateOrEditOtheExpenseForm from '../../pages/OtherExpenses/CreateOrEditExpense/CreateOrEditExpense';
import DeleteExpense from '../../pages/OtherExpenses/DeleteExpense/DeleteExpense';
import NoExpenses from './NoExpenses/NoExpenses';
import { OtherExpenseToDelete } from '../../pages/OtherExpenses/OtherExpenses';
import TwExpenseMobile from '../TwExpenseMobile/ExpensesMobile';
import { Theme } from '@mui/material/styles';
import LoadingExpenses from './LoadingExpenses/LoadingExpenses';

export interface TwExpensesTabProps {
  tripId?: string;
  readableTripId?: string;
  isDriverLink?: boolean;
  driver?: { id: string; name: string };
  token?: string;
  updateCounterTab: () => void;
}

const TwExpensesTab: FC<TwExpensesTabProps> = ({
  tripId,
  readableTripId,
  isDriverLink = false,
  driver,
  token,
  updateCounterTab
}): JSX.Element => {
  const [pageParams, setPageParams] = useState<SearchOtherExpensesParams | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<string | null>(null);
  const [selectedExpenseToDelete, setSelectedExpenseToDelete] =
    useState<OtherExpenseToDelete | null>(null);
  const [openCreateOtherExpense, setOpenCreateOtherExpense] = useState(false);
  const [openEditOtherExpense, setOpenEditOtherExpense] = useState(false);
  const [openViewOtherExpense, setOpenViewOtherExpense] = useState(false);
  const [openDeleteOtherExpense, setOpenDeleteOtherExpense] = useState(false);
  const [mutatedExpenses, setMutatedExpenses] = useState(false);

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // Fetch other expenses
  const { data: otherExpenses = { content: [], total: 0 }, isLoading } =
    useSearchOtherExpensesQuery(pageParams as SearchOtherExpensesParams, {
      skip: !tripId || !pageParams
    });

  useEffect(() => {
    if (!tripId) return;

    if (token) {
      setPageParams({
        token
      });

      return;
    }

    setPageParams({
      params: { offset: 0, limit: 50 },
      filters: {
        tripIds: [tripId as string]
      }
    });
  }, [tripId, token]);

  useEffect(() => {
    if (!mutatedExpenses) return;

    updateCounterTab();
  }, [otherExpenses.total, mutatedExpenses]);

  const handleOpenEditOtherExpenseDialog = (id: string): void => {
    setSelectedExpense(id);
    setOpenEditOtherExpense(true);
  };

  const handleOpenViewOtherExpenseDialog = (id: string): void => {
    setSelectedExpense(id);
    setOpenViewOtherExpense(true);
  };

  const handleOpenAddOtherExpenseDialog = (): void => {
    setOpenCreateOtherExpense(true);
  };

  const handleCloseAddOtherExpenseDialog = (changed: boolean): void => {
    setMutatedExpenses(changed);
    setOpenCreateOtherExpense(false);
  };

  const handleCloseEditOtherExpenseDialog = (): void => {
    setSelectedExpense(null);
    setOpenEditOtherExpense(false);
  };

  const handleCloseViewOtherExpenseDialog = (): void => {
    setSelectedExpense(null);
    setOpenViewOtherExpense(false);
  };

  const handleOpenDeleteOtherExpenseDialog = (
    id: string,
    expenseName: string,
    equipmentName?: string
  ): void => {
    setSelectedExpenseToDelete({ id, expenseName, equipmentName });
    setOpenDeleteOtherExpense(true);
  };

  const handleCloseDeleteOtherExpenseDialog = (changed: boolean): void => {
    setMutatedExpenses(changed);
    setSelectedExpenseToDelete(null);
    setOpenDeleteOtherExpense(false);
  };

  return (
    <>
      {isLoading ? (
        <LoadingExpenses />
      ) : (
        <Box className="expenses-tab">
          <Box component="div" className="expenses-tab__title">
            Expenses
          </Box>

          {otherExpenses.total === 0 ? (
            <NoExpenses
              label="No expenses added to the trip yet"
              tripId={tripId}
              readableTripId={readableTripId}
              token={token}
              isDriverLink={isDriverLink}
              driver={driver}
              setMutatedExpenses={setMutatedExpenses}
            />
          ) : matchesMobileOrTablet ? (
            <TwExpenseMobile
              otherExpenses={otherExpenses.content}
              onOpenEditOtherExpenseDialog={handleOpenEditOtherExpenseDialog}
              onOpenViewOtherExpenseDialog={handleOpenViewOtherExpenseDialog}
              onOpenAddOtherExpenseDialog={handleOpenAddOtherExpenseDialog}
              onOpenDeleteOtherExpenseDialog={handleOpenDeleteOtherExpenseDialog}
              isDriverLink={isDriverLink}
            />
          ) : (
            <OtherExpenseTable
              otherExpenses={otherExpenses.content}
              totalOtherExpenses={otherExpenses.total}
              onOpenEditOtherExpenseDialog={handleOpenEditOtherExpenseDialog}
              onOpenViewOtherExpenseDialog={handleOpenViewOtherExpenseDialog}
              onOpenAddOtherExpenseDialog={handleOpenAddOtherExpenseDialog}
              onOpenDeleteOtherExpenseDialog={handleOpenDeleteOtherExpenseDialog}
              isDriverLink={isDriverLink}
              isTab
            />
          )}

          {openCreateOtherExpense && (
            <CreateOrEditOtheExpenseForm
              open={openCreateOtherExpense}
              onClose={handleCloseAddOtherExpenseDialog}
              otherExpenseId={null}
              tripId={tripId}
              readableTripId={readableTripId}
              isDriverLink={isDriverLink}
              driver={driver}
              token={token}
            />
          )}

          {openEditOtherExpense && (
            <CreateOrEditOtheExpenseForm
              open={openEditOtherExpense}
              onClose={handleCloseEditOtherExpenseDialog}
              otherExpenseId={selectedExpense}
              tripId={tripId}
              readableTripId={readableTripId}
              isDriverLink={isDriverLink}
              token={token}
            />
          )}

          {openViewOtherExpense && (
            <CreateOrEditOtheExpenseForm
              open={openViewOtherExpense}
              onClose={handleCloseViewOtherExpenseDialog}
              otherExpenseId={selectedExpense}
              tripId={tripId}
              readableTripId={readableTripId}
              isDriverLink={isDriverLink}
              token={token}
              isView
            />
          )}

          {/* Delete other expense dialog */}
          {openDeleteOtherExpense && (
            <DeleteExpense
              open={openDeleteOtherExpense}
              expenseId={selectedExpenseToDelete?.id || null}
              expenseName={selectedExpenseToDelete?.expenseName || ''}
              equipmentName={selectedExpenseToDelete?.equipmentName}
              onClose={handleCloseDeleteOtherExpenseDialog}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default TwExpensesTab;
